<template>
  <wb-accordion-item
    class="retailer-selection-box"
    :class="[
      getSelectionBoxClass('retailer-selection-box'),
      'request-flow-selector',
    ]"
    :open="boxIsOpen"
    ref="accordionRef"
  >
    <wb-accordion-toggle class="retailer-selection-box__toggle" @click="toggle">
      <div class="retailer-selection-box__step">
        <span v-if="!showSuccessIcon">{{ step }}</span>
        <wb-icon
          v-else
          name="bds/checkmark/16"
          class="retailer-selection-box__step--success"
        ></wb-icon>
      </div>
      <div
        class="retailer-selection-box__label"
        :class="retailerSelectionBoxLabelFont"
      >
        <wb-icon name="bds/location-dealer/24"></wb-icon>
        <span v-if="showToggleLabel">{{ boxTitle }}</span>
      </div>
      <div class="retailer-selection-box__selected-value">
        <span v-if="showSuccessIcon" class="wb-type-copy">
          {{ selectedValue }}
        </span>
      </div>
    </wb-accordion-toggle>
    <wb-accordion-section class="mobile-overflow-wb-disable">
      <wb-accordion-mobile-header @click="closeAccordion(emit)">{{
        $t('schedule.filter.back')
      }}</wb-accordion-mobile-header>
      <wb-accordion-content
        :class="{
          'dealerWidget-with-top-notification': noDealersFoundInRequest,
        }"
      >
        <div
          class="retailer-selection-box__label__inModal"
          :class="retailerSelectionBoxLabelFont"
          v-if="isMobile || isMq3"
        >
          <wb-icon
            class="retailer-selection-box__label__inModal-icon"
            id="retailer-selection-box__label__inModal-icon"
            name="bds/location-dealer/24"
          ></wb-icon>
          <span
            class="retailer-selection-box__label__inModal-text wb-type-heading-s"
            >{{ boxTitle }}</span
          >
        </div>

        <wb-input-control
          class="retailer-selection-box__input"
          :class="noDealersFoundClass"
        >
          <wb-input @input="getSuggestions" @keypress.enter="selectLocation">
            <label>{{ $t('schedule.request.selectionBoxes.location') }}</label>
            <input
              type="text"
              v-model="locationText"
              @focusout="onBlurInput"
              ref="requestLocationInput"
            />
            <wb-input-action>
              <button
                class="wb-round-button wb-round-button--small"
                type="button"
              >
                <wb-icon
                  @click="selectLocation"
                  class="wb-round-button__icon retailer-selection-box__input__icon"
                  name="bds/search/24"
                />
              </button>
            </wb-input-action>
          </wb-input>
        </wb-input-control>

        <div
          class="wb-list-group"
          v-if="
            (addressSuggestion.length || locationText) && locationInputFocused
          "
        >
          <!-- TODO: Geolocation -->
          <!--<button
            type="button"
            class="wb-list-group__item wb-list-group__item-selected"
          >
            <wb-icon name="locate-me"></wb-icon>
            {{ $t('search.location.place.suggestion') }}
          </button>-->
          <button
            type="button"
            @click="selectLocation"
            class="wb-list-group__item"
          >
            {{ locationText }}
          </button>
          <button
            type="button"
            class="wb-list-group__item"
            v-for="suggestion in addressSuggestion"
            :key="suggestion.locationId"
            @click="selectLocation(suggestion)"
          >
            {{ suggestion.label }}
          </button>
        </div>
        <wb-notification
          v-if="noDealersFoundInRequest && widgetInitialized"
          icon="bds/info-circle/24"
          theme="dark"
          class="wb-margin-bottom-s schedule__no-results-notification"
          hide-dismiss-button
        >
          <wb-notification-content>
            {{ $t('schedule.request.selectionBoxes.noDealers.notification') }}
          </wb-notification-content>
        </wb-notification>
        <div
          class="retailer-selection-box__spinner-wrapper"
          v-if="!widgetInitialized || !widgetReady"
        >
          <wb-spinner class="retailer-selection-box__spinner"></wb-spinner>
        </div>
        <widget-loader
          v-if="widgetReady"
          id="dealerWidget"
          ref="dealerWidget"
          @resources-loaded="initializeDLWidget"
          :resources="dealerSearchResources"
        />
      </wb-accordion-content>
    </wb-accordion-section>
  </wb-accordion-item>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, onMounted, ref } from 'vue';

import useDealerLocator from '../../../composables/dealer-locator.composable';
import { WidgetLoader } from '../../index';

import useRequestSelectionBoxes from '../../../composables/request-selection-boxes.composable';

import { useMediaQuery } from '../../../composables/media-query.composable';
import useRequestSessionStorage from '../../../composables/request-session.composable';
import { useStore } from '../../../composables/store.composable';
import { LocationSuggestion } from '../../../graphql/schema';
import {
  RequestSelectionBoxesStatus,
  RequestSelectionBoxesStatusType,
  RequestSelectionBoxesStep,
} from '../../../models/entities/request-selection-boxes.model';
import { i18n } from '../../../services/translation.service';
import UtilsService from '../../../services/utils.service';

declare const DealerLocator: any;

export default defineComponent({
  i18n,
  name: 'otb-retailer-selection-box',
  components: {
    'widget-loader': WidgetLoader,
  },
  props: {
    status: {
      type: String as PropType<RequestSelectionBoxesStatusType>,
      default: RequestSelectionBoxesStatus.TODO,
    },
    step: {
      type: Number,
      default: 1,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    currentStep: String,
    accordionClosingHandler: {
      type: Function,
      default: () => () => {
        return;
      },
    },
  },
  setup(props, { emit }) {
    const { getter, state, dispatch, commit } = useStore();
    const { isMq3, browserDetect } = useMediaQuery();
    const { closeAccordion, selectionBoxIsDone, selectionBoxClass } =
      useRequestSelectionBoxes();

    const { dealerSearchResources, getWidgetBaseConfig } = useDealerLocator();

    const {
      getSessionStorageSelectedLocation,
      setSessionStorageSelectedLocation,
      getSessionStorageSelectedRetailer,
      setSessionStorageSelectedRetailer,
    } = useRequestSessionStorage();

    const dealerWidget = ref();

    const accordionRef = ref();

    const locationText = ref(
      state.otbcFlow.initialLocation?.label ||
        state.otbcFlow.requestLocationQueryFreeText ||
        state.otbcFlow.locationQueryFreeText
    );
    const suggestionSelected = ref(false);

    const widgetReady = ref(true);

    const widgetLoaded = ref(false);

    const locationInputFocused = ref(false);

    const widgetInitialized = ref(false);

    const requestLocationInput = ref(null);

    const isTestDriveFromHomeEnabled = computed(
      () => getter?.isTestDriveFromHomeEnabled
    );

    const showSuccessIcon = computed(() => {
      return selectionBoxIsDone(props.status);
    });

    const boxTitle = computed(() => {
      return props.status === RequestSelectionBoxesStatus.AVAILABLE
        ? i18n.t('schedule.request.selectionBoxes.retailerSelection')
        : i18n.t('schedule.request.selectionBoxes.retailerAvailable');
    });

    const freeTextLocationSearch = computed(() => {
      return `${i18n.t('schedule.request.selectionBoxes.freeTextSearch')} ${
        locationText.value
      } `;
    });

    const getSelectionBoxClass = (mainClass: string) => {
      return selectionBoxClass(props.status, mainClass);
    };

    const toggle = () => {
      emit('onToggle');
      const isOpen = accordionRef.value.hasAttribute('open');
      if (!isOpen) {
        props.accordionClosingHandler();
        UtilsService.closeAllAccordions();
        emit('open', 'retailer');
      }
    };

    const selectDealer = ($event, shouldSetSession = true) => {
      props.accordionClosingHandler();

      dispatch('selectDealer', { dealer: $event });
      emit('changeStatus');

      if (shouldSetSession) {
        setSessionStorageSelectedLocation(state?.otbcFlow?.requestLocation);
        setSessionStorageSelectedRetailer($event);
      }

      toggle();
      accordionRef.value.removeAttribute('open');
    };

    const handleOutsideInputClick = (e: FocusEvent): void => {
      const focusedElement = e.relatedTarget as HTMLElement;
      if (
        !focusedElement ||
        !focusedElement.classList.contains('wb-list-group__item')
      ) {
        locationInputFocused.value = false;
      }
    };

    const onBlurInput = (e: FocusEvent): void => {
      //workaround to handle behavior in IOS
      setTimeout(() => {
        handleOutsideInputClick(e);
      }, 250);
    };

    const debouncePeriod = browserDetect() === 'safari' ? 200 : 500;

    const getSuggestions = UtilsService.debounce((): void => {
      if (locationText.value && locationText.value.length >= 1) {
        locationInputFocused.value = true;
        commit('requestLocationQueryFreeText', locationText.value);
        dispatch('getAddressSuggestion', {
          address: locationText.value,
        });
      }
    }, debouncePeriod);

    const addressSuggestion = computed((): LocationSuggestion[] => {
      return state.otbcFlow.addressSuggestion;
    });

    const selectLocation = (suggestion: LocationSuggestion): void => {
      widgetReady.value = false;
      suggestionSelected.value = true;
      setTimeout((): void => {
        dispatch('selectRequestLocation', { suggestion });
        locationText.value =
          suggestion && suggestion.label
            ? suggestion.label
            : state.otbcFlow.requestLocationQueryFreeText ||
              state.otbcFlow.locationQueryFreeText;

        widgetReady.value = true;
        locationInputFocused.value = false;
        requestLocationInput.value.blur();
      }, 500);
    };

    const initializeDLWidget = async () => {
      await dispatch('getRequestTestDriveDealersWithinRadius');
      initWidget();
    };
    const initWidget = async () => {
      const locationId = state.otbcFlow.requestLocation?.locationId;
      const locationName =
        state.otbcFlow.requestLocationQueryFreeText ||
        state.otbcFlow.locationQueryFreeText;
      const countryCode = state.configuration.locale?.countryCode;
      const market = state.configuration.market;
      const otbcFlow = state.otbcFlow;
      const modelMapping = state.configuration.modelMapping;
      const dealerResults = state.otbcFlow.dealersSearchResults.length;
      const language = getter.localeCode.replace('-', '_');
      const widgetConfig = getWidgetBaseConfig(
        { language, countryCode, market, modelMapping },
        otbcFlow,
        getter.dealerSearchConfig
      );
      const requestMapHiddenFields = [
        'dl-search-input',
        'dl-search-title',
        'dl-search-filters',
      ];
      const dlHeight = dealerResults > 0 ? '300px' : '400px';
      const settings = {
        ...widgetConfig,
        element: dealerWidget.value.$el,
        hiddenFields: requestMapHiddenFields,
        language,
        height: `calc(100vh - ${dlHeight})`,
        removeMargins: true,
        selectDealerCallback: selectDealer,
      };
      if (locationId) {
        settings.initialSearch = {
          field: 'LOCATION_ID',
          value: locationId,
        };
      } else {
        settings.initialSearch = {
          field: 'LOCATION_NAME',
          value: locationName,
        };
      }
      // set up whitelisted dealers able to pick up from home
      if (isTestDriveFromHomeEnabled.value)
        settings.whiteList = getter?.testDriveFromHomeWhitelistedDealers;
      // in case it is multiple fixed dealers we filter the results
      else if (
        (state?.otbcFlow?.dealers &&
          typeof state.otbcFlow.dealers === 'string') ||
        state.otbcFlow.dealers instanceof String
      ) {
        settings.whiteList = state.otbcFlow.dealers.split(',') ?? [];
        // deleting initial search for multiple fixed dealers to prevent hiding dealers very far from each other
        if (settings.initialSearch) delete settings.initialSearch;
      }

      if (settings.whiteList && settings.whiteList.length === 0) {
        // as per the requirements if no white list dealers, show all dealers in country
        // deleting initial search to achieve that
        if (settings.initialSearch) delete settings.initialSearch;
        // deleting useless whitelist field
        delete settings.whiteList;
      }

      const dsw = await new DealerLocator(settings);
      dsw.init();
      setTimeout((): void => {
        widgetInitialized.value = true;
      }, 500);
    };

    const boxIsOpen = computed(() => {
      const isBoxOpen = props.currentStep == RequestSelectionBoxesStep.RETAILER;

      if (isBoxOpen) emit('onBoxOpen', RequestSelectionBoxesStep.RETAILER);
      return isBoxOpen;
    });

    const selectedValue = computed(() => {
      if (!state.otbcFlow.dealer) return '';
      const dealerName = state.otbcFlow.dealer?.dealerCardInfo?.dealerName1;
      const dealerAddressLine1 =
        state.otbcFlow.dealer?.dealerCardInfo?.addressLine1;
      const dealerAddressLine2 =
        state.otbcFlow.dealer?.dealerCardInfo?.addressLine2;
      const dealerAdressCountry = state.otbcFlow.dealer?.address?.country;
      const dealerAddress = i18n.t('request.dealerAddress', {
        dealerName,
        dealerAddressLine1,
        dealerAddressLine2,
        dealerAdressCountry,
      });
      return dealerAddress;
    });

    const retailerSelectionBoxLabelFont = computed(() => {
      return props.isMobile ? 'wb-type-copy-strong' : 'wb-type-heading-s';
    });

    const showToggleLabel = computed(() => {
      return (
        props.status === RequestSelectionBoxesStatus.AVAILABLE ||
        props.status === RequestSelectionBoxesStatus.TODO
      );
    });

    const noDealersFound = computed((): boolean => {
      return (
        state.otbcFlow.dealersSearchResults.length === 0 &&
        state.otbcFlow.requestDealersSearchResults.length === 0
      );
    });

    const noDealersFoundInRequest = computed((): boolean => {
      return (
        state.otbcFlow.requestDealersSearchResults.length === 0 &&
        state.otbcFlow.bookingSearchFinished
      );
    });

    const noDealersFoundClass = computed(() => {
      return {
        'retailer-selection-box__input__no-dealers':
          noDealersFound.value && widgetInitialized.value,
      };
    });

    const isLocationSelected = computed((): boolean => {
      return state.otbcFlow.locationSelected;
    });

    onMounted(async () => {
      const location = getSessionStorageSelectedLocation();
      const retailer = getSessionStorageSelectedRetailer();

      if (!location || !retailer) return;

      const isStoredLocationDifferentThanState =
        location?.label !== state?.otbcFlow?.initialLocation?.label;
      if (isStoredLocationDifferentThanState) setTimeout(toggle, 100);

      await selectLocation(location);
      await selectDealer(retailer, false);
    });

    return {
      noDealersFound,
      noDealersFoundInRequest,
      noDealersFoundClass,
      boxIsOpen,
      dealerSearchResources,
      boxTitle,
      freeTextLocationSearch,
      showSuccessIcon,
      dealerWidget,
      accordionRef,
      selectedValue,
      showToggleLabel,
      locationText,
      getSuggestions,
      addressSuggestion,
      widgetReady,
      widgetLoaded,
      closeAccordion,
      emit,
      initWidget,
      getSelectionBoxClass,
      toggle,
      selectLocation,
      retailerSelectionBoxLabelFont,
      locationInputFocused,
      onBlurInput,
      isLocationSelected,
      initializeDLWidget,
      widgetInitialized,
      isMq3,
      requestLocationInput,
    };
  },
});
</script>

<style scoped lang="scss" src="./retailer-selection-box.scss"></style>
